/**
 * Google Analytics Helper
 * Provides utilities for tracking events and page views
 */

/**
 * Check if gtag is available and Google Analytics is enabled
 */
const isGtagAvailable = () => {
  // Check if GA is disabled via environment variable
  const gaEnabled = process.env.REACT_APP_GA_ENABLED;
  if (gaEnabled === 'false') {
    return false;
  }
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Track a page view
 * @param {string} path - The page path
 * @param {string} title - The page title
 */
export const trackPageView = (path, title = '') => {
  if (isGtagAvailable()) {
    window.gtag('config', 'G-XM62R1WSCP', {
      page_path: path,
      page_title: title
    });
  }
};

/**
 * Track a custom event
 * @param {string} eventName - The event name
 * @param {object} eventParams - Additional event parameters (userId will be included if available)
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (isGtagAvailable()) {
    // Include userId if available from localStorage
    const localAuth =
      typeof window !== 'undefined' && window.localStorage
        ? JSON.parse(localStorage.getItem('localAuth') || '{}')
        : {};

    const enrichedParams = {
      ...eventParams,
      ...(localAuth.userId && { user_id: localAuth.userId })
    };

    window.gtag('event', eventName, enrichedParams);
  }
};

/**
 * Track user login
 * @param {string} method - Login method (e.g., 'email', 'google', etc.)
 */
export const trackLogin = (method = 'email') => {
  trackEvent('login', {
    method: method
  });
};

/**
 * Track user registration/sign up
 * @param {string} method - Registration method (e.g., 'email', 'google', etc.)
 */
export const trackSignUp = (method = 'email') => {
  trackEvent('sign_up', {
    method: method
  });
};

/**
 * Track job view
 * @param {string} jobId - The job ID
 * @param {string} jobTitle - The job title
 */
export const trackJobView = (jobId, jobTitle = '') => {
  trackEvent('view_job', {
    job_id: jobId,
    job_title: jobTitle
  });
};

/**
 * Track company view
 * @param {string} companyId - The company ID
 * @param {string} companyName - The company name
 */
export const trackCompanyView = (companyId, companyName = '') => {
  trackEvent('view_company', {
    company_id: companyId,
    company_name: companyName
  });
};

/**
 * Track job application
 * @param {string} jobId - The job ID
 * @param {string} jobTitle - The job title
 */
export const trackJobApplication = (jobId, jobTitle = '') => {
  trackEvent('apply_job', {
    job_id: jobId,
    job_title: jobTitle
  });
};

/**
 * Track search
 * @param {string} searchTerm - The search term
 */
export const trackSearch = (searchTerm) => {
  trackEvent('search', {
    search_term: searchTerm
  });
};

/**
 * Track plan selection
 * @param {string} planName - The plan name
 * @param {string} planValue - The plan value
 */
export const trackPlanSelection = (planName, planValue = '') => {
  trackEvent('select_plan', {
    plan_name: planName,
    plan_value: planValue
  });
};

/**
 * Track company creation
 * @param {string} companyId - The company ID
 * @param {string} companyName - The company name
 * @param {object} additionalData - Additional company data
 */
export const trackCompanyCreated = (
  companyId,
  companyName,
  additionalData = {}
) => {
  trackEvent('company_created', {
    company_id: companyId,
    company_name: companyName,
    ...additionalData
  });
};

/**
 * Track job creation
 * @param {string} jobId - The job ID
 * @param {string} jobTitle - The job title
 * @param {object} additionalData - Additional job data
 */
export const trackJobCreated = (jobId, jobTitle, additionalData = {}) => {
  trackEvent('job_created', {
    job_id: jobId,
    job_title: jobTitle,
    ...additionalData
  });
};

/**
 * Track purchase/payment completion
 * @param {string} transactionId - The transaction ID
 * @param {number} value - The purchase value
 * @param {string} currency - Currency code
 * @param {array} items - Array of purchased items
 * @param {object} additionalData - Additional purchase data
 */
export const trackPurchase = (
  transactionId,
  value,
  currency = 'USD',
  items = [],
  additionalData = {}
) => {
  trackEvent('purchase', {
    transaction_id: transactionId,
    value: value,
    currency: currency,
    items: items,
    ...additionalData
  });
};

/**
 * Track forgot password link click
 * @param {string} source - Where the link was clicked from (e.g., 'sign_in_page')
 */
export const trackForgotPasswordClick = (source = 'sign_in_page') => {
  trackEvent('forgot_password_click', {
    source: source
  });
};

/**
 * Track forgot password form submission
 * @param {string} email - The email address (masked for privacy)
 */
export const trackForgotPasswordSubmit = (email = '') => {
  // Mask email for privacy - show domain only
  const maskedEmail = email ? email.split('@')[1] || 'unknown' : 'unknown';
  trackEvent('forgot_password_submit', {
    email_domain: maskedEmail
  });
};

/**
 * Track forgot password success
 */
export const trackForgotPasswordSuccess = () => {
  trackEvent('forgot_password_success', {
    event_category: 'user_account',
    event_label: 'password_reset_requested'
  });
};

/**
 * Track forgot password error
 * @param {string} errorMessage - The error message (sanitized)
 */
export const trackForgotPasswordError = (errorMessage = '') => {
  trackEvent('forgot_password_error', {
    error_message: errorMessage,
    event_category: 'user_account'
  });
};

/**
 * Track reset password page view
 * @param {boolean} hasToken - Whether a valid token is present
 */
export const trackResetPasswordPageView = (hasToken = false) => {
  trackEvent('reset_password_page_view', {
    has_valid_token: hasToken,
    event_category: 'user_account'
  });
};

/**
 * Track reset password form submission
 */
export const trackResetPasswordSubmit = () => {
  trackEvent('reset_password_submit', {
    event_category: 'user_account'
  });
};

/**
 * Track reset password success
 */
export const trackResetPasswordSuccess = () => {
  trackEvent('reset_password_success', {
    event_category: 'user_account',
    event_label: 'password_reset_completed'
  });
};

/**
 * Track reset password error
 * @param {string} errorType - Type of error (e.g., 'expired_token', 'invalid_token', 'validation_error')
 */
export const trackResetPasswordError = (errorType = '') => {
  trackEvent('reset_password_error', {
    error_type: errorType,
    event_category: 'user_account'
  });
};

/**
 * Track account activation page view
 * @param {boolean} hasToken - Whether a valid token is present
 */
export const trackActivationPageView = (hasToken = false) => {
  trackEvent('activation_page_view', {
    has_valid_token: hasToken,
    event_category: 'user_account'
  });
};

/**
 * Track account activation button click
 */
export const trackActivationClick = () => {
  trackEvent('activation_click', {
    event_category: 'user_account'
  });
};

/**
 * Track account activation success
 */
export const trackActivationSuccess = () => {
  trackEvent('activation_success', {
    event_category: 'user_account',
    event_label: 'account_activated'
  });
};

/**
 * Track account activation error
 * @param {string} errorType - Type of error (e.g., 'expired_token', 'invalid_token', 'already_activated')
 */
export const trackActivationError = (errorType = '') => {
  trackEvent('activation_error', {
    error_type: errorType,
    event_category: 'user_account'
  });
};

/**
 * Track password change page view
 */
export const trackPasswordChangePageView = () => {
  trackEvent('password_change_page_view', {
    event_category: 'user_account'
  });
};

/**
 * Track password change form submission
 */
export const trackPasswordChangeSubmit = () => {
  trackEvent('password_change_submit', {
    event_category: 'user_account'
  });
};

/**
 * Track password change success
 */
export const trackPasswordChangeSuccess = () => {
  trackEvent('password_change_success', {
    event_category: 'user_account',
    event_label: 'password_changed'
  });
};

/**
 * Track password change error
 * @param {string} errorType - Type of error (e.g., 'incorrect_current_password', 'validation_error', 'same_password')
 */
export const trackPasswordChangeError = (errorType = '') => {
  trackEvent('password_change_error', {
    error_type: errorType,
    event_category: 'user_account'
  });
};

// ============================================
// PRICING PAGE EVENTS
// ============================================

/**
 * Track pricing page view
 */
export const trackPricingPageView = () => {
  trackPageView('/pricing', 'Pricing Page');
  trackEvent('view_pricing_page', {
    page_location: window.location.href
  });
};

/**
 * Track when user shows interest in base plan
 */
export const trackBasePlanInterest = () => {
  trackEvent('pricing_base_plan_interest', {
    plan_type: 'base',
    plan_name: 'Post a Job',
    plan_price: 599
  });
};

/**
 * Track when user shows interest in an add-on
 * @param {string} planName - Name of the add-on plan
 * @param {number} planId - ID of the plan
 * @param {number} price - Price of the add-on
 */
export const trackAddOnInterest = (planName, planId, price) => {
  trackEvent('pricing_addon_interest', {
    plan_id: planId,
    plan_name: planName,
    plan_price: price,
    addon_category:
      planId >= 5 && planId <= 7
        ? 'sticky'
        : planId === 4
        ? 'highlight'
        : planId === 2
        ? 'branding'
        : 'other'
  });
};

/**
 * Track pricing CTA click
 * @param {string} ctaLocation - Where the CTA was clicked (base_plan, final_cta)
 */
export const trackPricingCTAClick = (ctaLocation) => {
  trackEvent('pricing_cta_click', {
    cta_location: ctaLocation,
    destination: '/job/editor'
  });
};

/**
 * Track when user views add-ons section
 */
export const trackAddOnsSectionView = () => {
  trackEvent('pricing_addons_section_viewed', {
    section: 'premium_addons'
  });
};

// ============================================
// GEO-LOCK EVENTS
// ============================================

/**
 * Track when geo-lock plan is enabled
 * @param {string} jobId - The job ID
 * @param {string} selectedRegions - The selected regions
 */
export const trackGeoLockEnabled = (jobId = '', selectedRegions = '') => {
  trackEvent('geo_lock_enabled', {
    job_id: jobId,
    selected_regions: selectedRegions,
    event_category: 'job_management',
    event_label: 'geo_lock_activated'
  });
};

/**
 * Track when geo-lock plan is disabled
 * @param {string} jobId - The job ID
 */
export const trackGeoLockDisabled = (jobId = '') => {
  trackEvent('geo_lock_disabled', {
    job_id: jobId,
    event_category: 'job_management',
    event_label: 'geo_lock_deactivated'
  });
};

/**
 * Track when geo-lock confirmation dialog is shown
 * @param {string} jobId - The job ID
 * @param {string} selectedRegions - The selected regions
 */
export const trackGeoLockDialogShown = (jobId = '', selectedRegions = '') => {
  trackEvent('geo_lock_dialog_shown', {
    job_id: jobId,
    selected_regions: selectedRegions,
    event_category: 'job_management'
  });
};

/**
 * Track when user confirms geo-lock in dialog
 * @param {string} jobId - The job ID
 * @param {string} selectedRegions - The selected regions
 */
export const trackGeoLockConfirmed = (jobId = '', selectedRegions = '') => {
  trackEvent('geo_lock_confirmed', {
    job_id: jobId,
    selected_regions: selectedRegions,
    event_category: 'job_management',
    event_label: 'user_confirmed'
  });
};

/**
 * Track when user cancels geo-lock in dialog
 * @param {string} jobId - The job ID
 */
export const trackGeoLockCancelled = (jobId = '') => {
  trackEvent('geo_lock_cancelled', {
    job_id: jobId,
    event_category: 'job_management',
    event_label: 'user_cancelled'
  });
};

// ============================================
// DASHBOARD EVENTS
// ============================================

/**
 * Track dashboard page view
 * @param {number} jobCount - Number of jobs user has
 */
export const trackDashboardView = (jobCount = 0) => {
  trackEvent('dashboard_viewed', {
    job_count: jobCount,
    event_category: 'dashboard',
    has_jobs: jobCount > 0
  });
};

/**
 * Track when user activates a job
 * @param {string} jobId - The job ID
 * @param {string} jobTitle - The job title
 */
export const trackJobActivated = (jobId, jobTitle = '') => {
  trackEvent('job_activated', {
    job_id: jobId,
    job_title: jobTitle,
    event_category: 'job_management',
    event_label: 'status_changed_to_active'
  });
};

/**
 * Track when user deactivates a job
 * @param {string} jobId - The job ID
 * @param {string} jobTitle - The job title
 */
export const trackJobDeactivated = (jobId, jobTitle = '') => {
  trackEvent('job_deactivated', {
    job_id: jobId,
    job_title: jobTitle,
    event_category: 'job_management',
    event_label: 'status_changed_to_inactive'
  });
};

/**
 * Track when payment is required for job activation
 * @param {string} jobId - The job ID
 * @param {string} jobTitle - The job title
 */
export const trackJobActivationPaymentRequired = (jobId, jobTitle = '') => {
  trackEvent('job_activation_payment_required', {
    job_id: jobId,
    job_title: jobTitle,
    event_category: 'payment',
    event_label: 'payment_dialog_shown'
  });
};

/**
 * Track when user clicks "Buy Plan" from payment dialog
 * @param {string} jobId - The job ID
 * @param {string} jobTitle - The job title
 */
export const trackBuyPlanClicked = (jobId, jobTitle = '') => {
  trackEvent('buy_plan_clicked', {
    job_id: jobId,
    job_title: jobTitle,
    event_category: 'payment',
    event_label: 'redirected_to_plans',
    source: 'payment_dialog'
  });
};

/**
 * Track when user dismisses payment dialog
 * @param {string} jobId - The job ID
 */
export const trackPaymentDialogDismissed = (jobId = '') => {
  trackEvent('payment_dialog_dismissed', {
    job_id: jobId,
    event_category: 'payment',
    event_label: 'user_cancelled'
  });
};

/**
 * Track when user clicks "Post Your First Job" button
 */
export const trackPostFirstJobClicked = () => {
  trackEvent('post_first_job_clicked', {
    event_category: 'dashboard',
    event_label: 'empty_state_cta',
    source: 'dashboard_empty_state'
  });
};

/**
 * Track when user edits a job from dashboard
 * @param {string} jobId - The job ID
 * @param {string} jobTitle - The job title
 */
export const trackJobEditClicked = (jobId, jobTitle = '') => {
  trackEvent('job_edit_clicked', {
    job_id: jobId,
    job_title: jobTitle,
    event_category: 'job_management',
    source: 'dashboard'
  });
};

/**
 * Track when user deletes a job from dashboard
 * @param {string} jobId - The job ID
 * @param {string} jobTitle - The job title
 */
export const trackJobDeleted = (jobId, jobTitle = '') => {
  trackEvent('job_deleted', {
    job_id: jobId,
    job_title: jobTitle,
    event_category: 'job_management',
    event_label: 'job_removed'
  });
};

/**
 * Track when delete job confirmation dialog is shown.
 * @param {string} jobId - The job ID.
 * @param {string} jobTitle - The job title.
 * @param {boolean} isActive - Whether the job is currently active.
 */
export const trackDeleteDialogShown = (
  jobId,
  jobTitle = '',
  isActive = false
) => {
  trackEvent('delete_dialog_shown', {
    job_id: jobId,
    job_title: jobTitle,
    is_active: isActive,
    event_category: 'job_management',
    event_label: 'delete_confirmation_prompt'
  });
};

/**
 * Track when user chooses to keep the job instead of deleting.
 * @param {string} jobId - The job ID.
 */
export const trackDeleteDialogKept = (jobId) => {
  trackEvent('delete_dialog_kept', {
    job_id: jobId,
    event_category: 'job_management',
    event_label: 'user_kept_job'
  });
};

/**
 * Track when user chooses to deactivate instead of delete.
 * @param {string} jobId - The job ID.
 * @param {string} jobTitle - The job title.
 */
export const trackDeactivateInsteadOfDelete = (jobId, jobTitle = '') => {
  trackEvent('deactivate_instead_of_delete', {
    job_id: jobId,
    job_title: jobTitle,
    event_category: 'job_management',
    event_label: 'chose_deactivation_over_deletion'
  });
};

// ============================================
// APPLICATIONS / CANDIDATES EVENTS
// ============================================

/**
 * Track applications page view
 * @param {number} applicationCount - Total number of applications
 * @param {string} jobId - Optional job ID if viewing applications for a specific job
 * @param {string} viewType - Type of view (kanban, list, etc.)
 */
export const trackApplicationsPageView = (
  applicationCount = 0,
  jobId = null,
  viewType = 'kanban'
) => {
  trackEvent('applications_page_viewed', {
    application_count: applicationCount,
    job_id: jobId,
    view_type: viewType,
    event_category: 'applications',
    has_applications: applicationCount > 0
  });
};

/**
 * Track when user starts dragging a candidate card
 * @param {string} applicationId - The application ID
 * @param {string} candidateName - The candidate's name
 * @param {string} currentStatus - Current status of the application
 * @param {string} jobId - Job ID the application is for
 */
export const trackCandidateDragStart = (
  applicationId,
  candidateName = '',
  currentStatus = '',
  jobId = ''
) => {
  trackEvent('candidate_drag_start', {
    application_id: applicationId,
    candidate_name: candidateName,
    current_status: currentStatus,
    job_id: jobId,
    event_category: 'applications',
    event_label: 'candidate_drag_initiated'
  });
};

/**
 * Track when user moves a candidate to a new status
 * @param {string} applicationId - The application ID
 * @param {string} candidateName - The candidate's name
 * @param {string} fromStatus - Previous status
 * @param {string} toStatus - New status
 * @param {string} jobId - Job ID the application is for
 */
export const trackCandidateStatusChanged = (
  applicationId,
  candidateName = '',
  fromStatus = '',
  toStatus = '',
  jobId = ''
) => {
  trackEvent('candidate_status_changed', {
    application_id: applicationId,
    candidate_name: candidateName,
    from_status: fromStatus,
    to_status: toStatus,
    job_id: jobId,
    event_category: 'applications',
    event_label: `moved_from_${fromStatus}_to_${toStatus}`
  });
};

/**
 * Track when user clicks to view a candidate's profile
 * @param {string} applicationId - The application ID
 * @param {string} candidateId - The candidate's user ID
 * @param {string} candidateName - The candidate's name
 * @param {string} currentStatus - Current status of the application
 * @param {string} source - Where the action was triggered from (e.g., 'kanban_card', 'applications_list')
 */
export const trackCandidateProfileView = (
  applicationId,
  candidateId = '',
  candidateName = '',
  currentStatus = '',
  source = 'kanban_card'
) => {
  trackEvent('candidate_profile_viewed', {
    application_id: applicationId,
    candidate_id: candidateId,
    candidate_name: candidateName,
    current_status: currentStatus,
    source: source,
    event_category: 'applications',
    event_label: 'view_profile_clicked'
  });
};

/**
 * Track when user clicks to message a candidate
 * @param {string} applicationId - The application ID
 * @param {string} candidateId - The candidate's user ID
 * @param {string} candidateName - The candidate's name
 * @param {string} currentStatus - Current status of the application
 * @param {string} source - Where the action was triggered from
 */
export const trackCandidateMessageClick = (
  applicationId,
  candidateId = '',
  candidateName = '',
  currentStatus = '',
  source = 'kanban_card'
) => {
  trackEvent('candidate_message_clicked', {
    application_id: applicationId,
    candidate_id: candidateId,
    candidate_name: candidateName,
    current_status: currentStatus,
    source: source,
    event_category: 'applications',
    event_label: 'message_candidate_initiated'
  });
};

/**
 * Track when user views candidate applications for a specific job
 * @param {string} jobId - The job ID
 * @param {string} jobTitle - The job title
 * @param {number} applicationCount - Number of applications for this job
 */
export const trackJobApplicationsView = (
  jobId,
  jobTitle = '',
  applicationCount = 0
) => {
  trackEvent('job_applications_viewed', {
    job_id: jobId,
    job_title: jobTitle,
    application_count: applicationCount,
    event_category: 'applications',
    event_label: 'filtered_by_job'
  });
};

/**
 * Track when a candidate card is clicked/interacted with
 * @param {string} applicationId - The application ID
 * @param {string} candidateName - The candidate's name
 * @param {string} currentStatus - Current status of the application
 * @param {string} interactionType - Type of interaction (e.g., 'card_click', 'expand', etc.)
 */
export const trackCandidateCardInteraction = (
  applicationId,
  candidateName = '',
  currentStatus = '',
  interactionType = 'card_click'
) => {
  trackEvent('candidate_card_interaction', {
    application_id: applicationId,
    candidate_name: candidateName,
    current_status: currentStatus,
    interaction_type: interactionType,
    event_category: 'applications'
  });
};

/**
 * Track applications board column statistics
 * @param {object} statusCounts - Object with counts per status (e.g., {pending: 5, reviewed: 3})
 */
export const trackApplicationsStatusDistribution = (statusCounts = {}) => {
  trackEvent('applications_status_distribution', {
    ...statusCounts,
    total_applications: Object.values(statusCounts).reduce(
      (sum, count) => sum + count,
      0
    ),
    event_category: 'applications',
    event_label: 'status_distribution_snapshot'
  });
};

// ============================================
// MESSAGING EVENTS
// ============================================

/**
 * Track messages page view
 * @param {number} conversationCount - Number of conversations the user has
 */
export const trackMessagesPageView = (conversationCount = 0) => {
  trackPageView('/messages', 'Messages Page');
  trackEvent('messages_page_viewed', {
    conversation_count: conversationCount,
    event_category: 'messaging',
    has_conversations: conversationCount > 0
  });
};

/**
 * Track when user opens a conversation
 * @param {string} conversationId - The conversation ID
 * @param {string} otherUserName - Name of the other user in the conversation
 * @param {boolean} hasUnreadMessages - Whether the conversation had unread messages
 */
export const trackConversationOpened = (
  conversationId,
  otherUserName = '',
  hasUnreadMessages = false
) => {
  trackEvent('conversation_opened', {
    conversation_id: conversationId,
    other_user_name: otherUserName,
    had_unread_messages: hasUnreadMessages,
    event_category: 'messaging'
  });
};

/**
 * Track when user sends a message
 * @param {string} conversationId - The conversation ID
 * @param {number} messageLength - Length of the message content
 */
export const trackMessageSent = (conversationId, messageLength = 0) => {
  trackEvent('message_sent', {
    conversation_id: conversationId,
    message_length: messageLength,
    event_category: 'messaging',
    event_label: 'user_sent_message'
  });
};

/**
 * Track when a new conversation is started
 * @param {string} conversationId - The new conversation ID
 * @param {string} recipientType - Type of recipient ('candidate' or 'company')
 * @param {string} source - Where the conversation was started from (e.g., 'applications_list')
 */
export const trackConversationStarted = (
  conversationId,
  recipientType = '',
  source = ''
) => {
  trackEvent('conversation_started', {
    conversation_id: conversationId,
    recipient_type: recipientType,
    source: source,
    event_category: 'messaging',
    event_label: 'new_conversation_created'
  });
};

/**
 * Track when user receives a new message (via WebSocket)
 * @param {string} conversationId - The conversation ID
 * @param {boolean} isViewingConversation - Whether user is currently viewing the conversation
 */
export const trackMessageReceived = (
  conversationId,
  isViewingConversation = false
) => {
  trackEvent('message_received', {
    conversation_id: conversationId,
    is_viewing_conversation: isViewingConversation,
    event_category: 'messaging'
  });
};

/**
 * Track when user marks messages as read
 * @param {string} conversationId - The conversation ID
 * @param {number} unreadCount - Number of messages marked as read
 */
export const trackMessagesRead = (conversationId, unreadCount = 0) => {
  trackEvent('messages_marked_read', {
    conversation_id: conversationId,
    messages_read_count: unreadCount,
    event_category: 'messaging'
  });
};

/**
 * Track WebSocket connection status
 * @param {string} status - Connection status ('connected', 'disconnected', 'error', 'reconnecting')
 */
export const trackWebSocketStatus = (status) => {
  trackEvent('websocket_status', {
    connection_status: status,
    event_category: 'messaging',
    event_label: `websocket_${status}`
  });
};

// ============================================
// CANDIDATE PROFILE EVENTS
// ============================================

/**
 * Track candidate profile page view (own profile)
 * @param {string} userId - The user's ID
 * @param {boolean} hasPhoto - Whether user has a profile photo
 * @param {boolean} hasExperience - Whether user has added experience
 * @param {boolean} hasSkills - Whether user has added skills
 * @param {number} experienceCount - Number of experiences
 * @param {number} skillCount - Number of skills
 */
export const trackCandidateOwnProfileView = (
  userId = '',
  hasPhoto = false,
  hasExperience = false,
  hasSkills = false,
  experienceCount = 0,
  skillCount = 0
) => {
  trackPageView('/profile', 'Candidate Profile');
  trackEvent('candidate_own_profile_viewed', {
    candidate_id: userId,
    has_photo: hasPhoto,
    has_experience: hasExperience,
    has_skills: hasSkills,
    experience_count: experienceCount,
    skill_count: skillCount,
    event_category: 'candidate_profile',
    profile_completeness:
      (hasPhoto ? 1 : 0) + (hasExperience ? 1 : 0) + (hasSkills ? 1 : 0)
  });
};

/**
 * Track when candidate starts editing their intro section
 */
export const trackProfileIntroEditStart = () => {
  trackEvent('profile_intro_edit_start', {
    event_category: 'candidate_profile',
    event_label: 'intro_editing_initiated'
  });
};

/**
 * Track when candidate saves their intro section
 * @param {boolean} hasHeadline - Whether headline was added
 * @param {boolean} hasPosition - Whether position was added
 * @param {boolean} hasEducation - Whether education was added
 * @param {boolean} hasLocation - Whether location was added
 */
export const trackProfileIntroSaved = (
  hasHeadline = false,
  hasPosition = false,
  hasEducation = false,
  hasLocation = false
) => {
  trackEvent('profile_intro_saved', {
    has_headline: hasHeadline,
    has_position: hasPosition,
    has_education: hasEducation,
    has_location: hasLocation,
    fields_filled:
      (hasHeadline ? 1 : 0) +
      (hasPosition ? 1 : 0) +
      (hasEducation ? 1 : 0) +
      (hasLocation ? 1 : 0),
    event_category: 'candidate_profile',
    event_label: 'intro_section_updated'
  });
};

/**
 * Track when candidate starts editing their about section
 */
export const trackProfileAboutEditStart = () => {
  trackEvent('profile_about_edit_start', {
    event_category: 'candidate_profile',
    event_label: 'about_editing_initiated'
  });
};

/**
 * Track when candidate saves their about section
 * @param {number} wordCount - Number of words in the about section
 */
export const trackProfileAboutSaved = (wordCount = 0) => {
  trackEvent('profile_about_saved', {
    word_count: wordCount,
    has_content: wordCount > 0,
    content_length:
      wordCount < 50 ? 'short' : wordCount < 150 ? 'medium' : 'long',
    event_category: 'candidate_profile',
    event_label: 'about_section_updated'
  });
};

/**
 * Track when candidate uploads a profile photo
 * @param {string} fileType - The file type (e.g., 'image/jpeg')
 * @param {number} fileSize - File size in bytes
 */
export const trackProfilePhotoUploaded = (fileType = '', fileSize = 0) => {
  trackEvent('profile_photo_uploaded', {
    file_type: fileType,
    file_size_kb: Math.round(fileSize / 1024),
    event_category: 'candidate_profile',
    event_label: 'photo_added'
  });
};

/**
 * Track when candidate deletes their profile photo
 */
export const trackProfilePhotoDeleted = () => {
  trackEvent('profile_photo_deleted', {
    event_category: 'candidate_profile',
    event_label: 'photo_removed'
  });
};

/**
 * Track when candidate opens the experience dialog
 * @param {boolean} isEditing - Whether editing existing experience or adding new
 */
export const trackProfileExperienceDialogOpened = (isEditing = false) => {
  trackEvent('profile_experience_dialog_opened', {
    action_type: isEditing ? 'edit' : 'add',
    event_category: 'candidate_profile',
    event_label: isEditing ? 'edit_experience' : 'add_experience'
  });
};

/**
 * Track when candidate saves an experience
 * @param {boolean} isEditing - Whether editing or adding new
 * @param {boolean} isCurrentRole - Whether it's a current role
 * @param {boolean} hasDescription - Whether description was added
 */
export const trackProfileExperienceSaved = (
  isEditing = false,
  isCurrentRole = false,
  hasDescription = false
) => {
  trackEvent('profile_experience_saved', {
    action_type: isEditing ? 'edit' : 'add',
    is_current_role: isCurrentRole,
    has_description: hasDescription,
    event_category: 'candidate_profile',
    event_label: isEditing ? 'experience_updated' : 'experience_added'
  });
};

/**
 * Track when candidate deletes an experience
 */
export const trackProfileExperienceDeleted = () => {
  trackEvent('profile_experience_deleted', {
    event_category: 'candidate_profile',
    event_label: 'experience_removed'
  });
};

/**
 * Track when candidate opens the skill dialog
 */
export const trackProfileSkillDialogOpened = () => {
  trackEvent('profile_skill_dialog_opened', {
    event_category: 'candidate_profile',
    event_label: 'add_skill'
  });
};

/**
 * Track when candidate adds a skill
 * @param {string} skillName - The skill name
 */
export const trackProfileSkillAdded = (skillName = '') => {
  trackEvent('profile_skill_added', {
    skill_name: skillName,
    event_category: 'candidate_profile',
    event_label: 'skill_added'
  });
};

/**
 * Track when candidate deletes a skill
 * @param {string} skillName - The skill name
 */
export const trackProfileSkillDeleted = (skillName = '') => {
  trackEvent('profile_skill_deleted', {
    skill_name: skillName,
    event_category: 'candidate_profile',
    event_label: 'skill_removed'
  });
};

// ============================================
// PUBLIC CANDIDATE PROFILE VIEW EVENTS (Recruiter viewing candidate)
// ============================================

/**
 * Track when someone views a candidate's public profile
 * @param {string} candidateId - The candidate's user ID
 * @param {string} candidateName - The candidate's name
 * @param {string} source - Where the view came from (e.g., 'applications_list', 'search_results')
 * @param {boolean} hasExperience - Whether candidate has experience listed
 * @param {boolean} hasSkills - Whether candidate has skills listed
 * @param {number} experienceCount - Number of experiences
 * @param {number} skillCount - Number of skills
 */
export const trackPublicCandidateProfileViewed = (
  candidateId = '',
  candidateName = '',
  source = 'unknown',
  hasExperience = false,
  hasSkills = false,
  experienceCount = 0,
  skillCount = 0
) => {
  trackEvent('public_candidate_profile_viewed', {
    candidate_id: candidateId,
    candidate_name: candidateName,
    source: source,
    has_experience: hasExperience,
    has_skills: hasSkills,
    experience_count: experienceCount,
    skill_count: skillCount,
    profile_completeness: (hasExperience ? 1 : 0) + (hasSkills ? 1 : 0),
    event_category: 'candidate_profile_view',
    event_label: 'recruiter_viewed_profile'
  });
};

/**
 * Track when recruiter clicks on candidate's LinkedIn profile
 * @param {string} candidateId - The candidate's user ID
 * @param {string} candidateName - The candidate's name
 */
export const trackCandidateLinkedInClicked = (
  candidateId = '',
  candidateName = ''
) => {
  trackEvent('candidate_linkedin_clicked', {
    candidate_id: candidateId,
    candidate_name: candidateName,
    event_category: 'candidate_profile_view',
    event_label: 'linkedin_link_clicked'
  });
};

/**
 * Track when recruiter clicks on candidate's email
 * @param {string} candidateId - The candidate's user ID
 * @param {string} candidateName - The candidate's name
 */
export const trackCandidateEmailClicked = (
  candidateId = '',
  candidateName = ''
) => {
  trackEvent('candidate_email_clicked', {
    candidate_id: candidateId,
    candidate_name: candidateName,
    event_category: 'candidate_profile_view',
    event_label: 'email_link_clicked'
  });
};

/**
 * Track profile section interactions
 * @param {string} candidateId - The candidate's user ID
 * @param {string} sectionName - Section that was interacted with (about, experience, skills)
 * @param {string} interactionType - Type of interaction (view, expand, etc.)
 */
export const trackCandidateProfileSectionInteraction = (
  candidateId = '',
  sectionName = '',
  interactionType = 'view'
) => {
  trackEvent('candidate_profile_section_interaction', {
    candidate_id: candidateId,
    section_name: sectionName,
    interaction_type: interactionType,
    event_category: 'candidate_profile_view'
  });
};

// ============================================
// INTERVIEW SCHEDULING EVENTS
// ============================================

/**
 * Track interviews page view
 * @param {number} interviewCount - Number of scheduled interviews
 */
export const trackInterviewsPageView = (interviewCount = 0) => {
  trackPageView('/interviews', 'Interviews Calendar');
  trackEvent('interviews_page_viewed', {
    interview_count: interviewCount,
    event_category: 'interviews',
    has_interviews: interviewCount > 0
  });
};

/**
 * Track when schedule interview dialog is opened
 * @param {string} candidateId - The candidate's user ID
 * @param {string} candidateName - The candidate's name
 * @param {string} jobId - The job ID
 * @param {string} jobTitle - The job title
 * @param {string} source - Where the dialog was opened from (e.g., 'application_card')
 */
export const trackScheduleInterviewDialogOpened = (
  candidateId = '',
  candidateName = '',
  jobId = '',
  jobTitle = '',
  source = 'application_card'
) => {
  trackEvent('schedule_interview_dialog_opened', {
    candidate_id: candidateId,
    candidate_name: candidateName,
    job_id: jobId,
    job_title: jobTitle,
    source: source,
    event_category: 'interviews',
    event_label: 'dialog_opened'
  });
};

/**
 * Track when an interview is successfully scheduled
 * @param {string} candidateId - The candidate's user ID
 * @param {string} candidateName - The candidate's name
 * @param {string} jobId - The job ID
 * @param {string} jobTitle - The job title
 * @param {number} durationMinutes - Interview duration in minutes
 * @param {string} locationType - Type of location (url, physical, etc.)
 */
export const trackInterviewScheduled = (
  candidateId = '',
  candidateName = '',
  jobId = '',
  jobTitle = '',
  durationMinutes = 60,
  locationType = 'url'
) => {
  trackEvent('interview_scheduled', {
    candidate_id: candidateId,
    candidate_name: candidateName,
    job_id: jobId,
    job_title: jobTitle,
    duration_minutes: durationMinutes,
    location_type: locationType,
    event_category: 'interviews',
    event_label: 'interview_created'
  });
};

/**
 * Track when schedule interview dialog is cancelled
 * @param {string} candidateId - The candidate's user ID
 * @param {string} candidateName - The candidate's name
 */
export const trackScheduleInterviewCancelled = (
  candidateId = '',
  candidateName = ''
) => {
  trackEvent('schedule_interview_cancelled', {
    candidate_id: candidateId,
    candidate_name: candidateName,
    event_category: 'interviews',
    event_label: 'dialog_cancelled'
  });
};

/**
 * Track when an interview calendar event is clicked
 * @param {string} interviewId - The interview ID
 * @param {string} candidateName - The candidate's name
 * @param {string} jobTitle - The job title
 */
export const trackInterviewEventClicked = (
  interviewId = '',
  candidateName = '',
  jobTitle = ''
) => {
  trackEvent('interview_event_clicked', {
    interview_id: interviewId,
    candidate_name: candidateName,
    job_title: jobTitle,
    event_category: 'interviews',
    event_label: 'calendar_event_clicked'
  });
};

/**
 * Track when interview status is updated
 * @param {string} interviewId - The interview ID
 * @param {string} oldStatus - Previous status
 * @param {string} newStatus - New status
 */
export const trackInterviewStatusUpdated = (
  interviewId = '',
  oldStatus = '',
  newStatus = ''
) => {
  trackEvent('interview_status_updated', {
    interview_id: interviewId,
    old_status: oldStatus,
    new_status: newStatus,
    event_category: 'interviews',
    event_label: `status_changed_to_${newStatus}`
  });
};

/**
 * Track interview scheduling errors
 * @param {string} errorType - Type of error
 * @param {string} errorMessage - Error message
 */
export const trackInterviewSchedulingError = (
  errorType = '',
  errorMessage = ''
) => {
  trackEvent('interview_scheduling_error', {
    error_type: errorType,
    error_message: errorMessage,
    event_category: 'interviews',
    event_label: 'scheduling_failed'
  });
};

// ============================================
// HOMEPAGE CTA EVENTS
// ============================================

/**
 * Track homepage CTA clicks
 * @param {string} section - Section name (e.g., 'platform_features', 'ai_banner')
 * @param {string} ctaLabel - CTA label identifier (e.g., 'post_a_job')
 * @param {string} destination - Destination path or anchor
 */
export const trackHomepageCtaClick = (
  section = '',
  ctaLabel = '',
  destination = ''
) => {
  trackEvent('homepage_cta_click', {
    section: section,
    cta_label: ctaLabel,
    destination: destination,
    event_category: 'homepage',
    page_location:
      typeof window !== 'undefined' ? window.location.href : undefined
  });
};

// ============================================
// TOPBAR NAVIGATION EVENTS
// ============================================

/**
 * Track topbar navigation interactions
 * @param {string} label - Label for the clicked item
 * @param {string} destination - Destination path or URL
 * @param {string} menu - Menu or section name
 * @param {string} action - Interaction type (click, open_menu)
 */
export const trackTopbarClick = (
  label = '',
  destination = '',
  menu = '',
  action = 'click'
) => {
  trackEvent('topbar_click', {
    label: label,
    destination: destination,
    menu: menu,
    action: action,
    event_category: 'navigation',
    page_location:
      typeof window !== 'undefined' ? window.location.href : undefined
  });
};
